import { useEffect, useState } from "react";
import { Smartphone, Share, Plus, MoreHorizontal, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

const DISMISS_KEY = "s1_install_blocker_dismissed_until";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const VISIT_COUNT_KEY = "s1_install_blocker_visits";
const MIN_VISITS_BEFORE_BLOCK = 2;

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const navAny = window.navigator as Navigator & { standalone?: boolean };
  if (navAny.standalone === true) return true; // iOS Safari
  return window.matchMedia("(display-mode: standalone)").matches;
}

function detectPlatform(): "ios" | "android" | "other" {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return "other";
}

function isDismissed(): boolean {
  if (typeof localStorage === "undefined") return false;
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const until = Number(raw);
  if (Number.isNaN(until)) return false;
  return until > Date.now();
}

function incrementAndCheckVisits(): boolean {
  try {
    const current = parseInt(localStorage.getItem(VISIT_COUNT_KEY) ?? "0", 10);
    const next = Number.isFinite(current) ? current + 1 : 1;
    localStorage.setItem(VISIT_COUNT_KEY, String(next));
    return next >= MIN_VISITS_BEFORE_BLOCK;
  } catch {
    return false;
  }
}

/**
 * InstallBlocker — Blocker plein écran qui pousse l'agent à installer la PWA
 * sur mobile. Sur iOS Safari, sans installation, les push notifs ne fonctionnent
 * PAS — donc tout l'intérêt de Juliette V3 (briefing matin, hot signals) tombe.
 *
 * Affichage conditionnel :
 *   - desktop → JAMAIS
 *   - mobile en standalone (déjà installé) → JAMAIS
 *   - mobile + dismiss localStorage actif (< 7j) → masqué
 *   - sinon → overlay plein écran avec instructions par OS
 *
 * Bouton "Plus tard" écrit un timestamp localStorage pour 7j (compromis bêta —
 * laisse aux utilisateurs un échappatoire le temps de l'adoption).
 */
export function InstallBlocker(): JSX.Element | null {
  const isMobile = useIsMobile();
  const [standalone, setStandalone] = useState<boolean>(() => isStandalone());
  const [dismissed, setDismissed] = useState<boolean>(() => isDismissed());
  const [hasEnoughVisits, setHasEnoughVisits] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(display-mode: standalone)");
    const onChange = () => setStandalone(isStandalone());
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (isMobile && !standalone && !dismissed) {
      setHasEnoughVisits(incrementAndCheckVisits());
    }
  }, [isMobile, standalone, dismissed]);

  if (!isMobile || standalone || dismissed || !hasEnoughVisits) return null;

  const platform = detectPlatform();

  function handleLater(): void {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DURATION_MS));
    }
    setDismissed(true);
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 backdrop-blur-md p-6">
      <div className="max-w-sm w-full space-y-6 rounded-2xl border border-border bg-card shadow-xl p-6">
        <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-indigo-500 to-purple-600">
          <Smartphone className="h-8 w-8 text-white" />
        </div>

        <div className="text-center space-y-2">
          <h2 className="text-xl font-bold">Installe l'app pour activer Juliette</h2>
          <p className="text-sm text-muted-foreground">
            Juliette a besoin d'être installée pour t'envoyer les briefings matin
            et les signaux chauds. Sans installation, pas de notifications.
          </p>
        </div>

        {platform === "ios" && (
          <div className="rounded-lg bg-muted/50 p-4 space-y-3 text-sm">
            <p className="font-semibold">Sur iPhone (Safari) :</p>
            <ol className="space-y-2 list-decimal list-inside text-muted-foreground">
              <li>
                Appuie sur <MoreHorizontal className="inline h-4 w-4 align-text-bottom" /> (les 3 points)
                en bas à droite
              </li>
              <li>
                Choisis <Share className="inline h-4 w-4 align-text-bottom" />{" "}
                <span className="font-medium text-foreground">"Partager"</span>
              </li>
              <li>
                Fais défiler puis tape <ChevronDown className="inline h-4 w-4 align-text-bottom" />{" "}
                <span className="font-medium text-foreground">"Voir plus"</span> (en bas)
              </li>
              <li>
                Choisis <span className="font-medium text-foreground">"Sur l'écran d'accueil"</span>
                <Plus className="inline h-4 w-4 ml-1 align-text-bottom" />
              </li>
              <li>Ouvre l'app depuis l'icône Système 1</li>
            </ol>
          </div>
        )}

        {platform === "android" && (
          <div className="rounded-lg bg-muted/50 p-4 space-y-3 text-sm">
            <p className="font-semibold">Sur Android (Chrome) :</p>
            <ol className="space-y-2 list-decimal list-inside text-muted-foreground">
              <li>Ouvre le menu Chrome (⋮ en haut à droite)</li>
              <li>
                Choisis <span className="font-medium text-foreground">"Installer l'application"</span>
              </li>
              <li>Ouvre l'app depuis l'icône Système 1</li>
            </ol>
          </div>
        )}

        {platform === "other" && (
          <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
            Ouvre cette page dans Safari (iPhone) ou Chrome (Android) pour
            installer l'app.
          </div>
        )}

        <Button onClick={handleLater} variant="ghost" className="w-full">
          Plus tard (7 jours)
        </Button>
      </div>
    </div>
  );
}
